import RadioboxElm from 'atoms/RadioboxElm';
import ButtonCta from 'atoms/ButtonCta';
import PlanDisplay from 'aa/vue/components/account/pages/accountSwitchPlan/variants/parts/PlanDisplay';
import { mapState } from 'vuex';
import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
import AccountService from 'aa/vue/services/AccountService';
import { logType, pageType } from 'aa/services/AccountPageTrackingService';
import { AA_XHR_BASE_PATH } from 'aa/vue/constants/aaRoutes';
import currentSubscriptionMixin from 'aa/vue/components/account/currentSubscriptionMixin';
import Product from 'aa/vue/models/Product';

export default {
    mixins: [currentSubscriptionMixin],
    components: {
        ButtonCta,
        RadioboxElm,
        PlanDisplay,
    },
    data() {
        return {
            isProcessing: false,
            canSwitchPlan: true,
            selectedID: null,
            isValidPromoList: {},
        };
    },
    computed: {
        ...mapState('AccountSwitchPlan', {
            userProduct: (state) => state.userProduct,
            promo: (state) => state.promo,
            authToken: (state) => state.authToken,
        }),

        cancelConfig() {
            return {
                styleClass: ['button', 'secondary'],
                displayText: this.content.get(
                    'ap_switch_plan_negative_cta',
                    this.$getLocale('cancel'),
                ),
                event: this.cancelOnClicked,
                disabled: this.isProcessing,
            };
        },
        submitConfig() {
            return {
                styleClass: ['button', 'primary'],
                displayText: this.content.get(
                    'ap_switch_plan_positive_cta',
                    this.$getLocale('switch_plan'),
                ),
                event: this.submitOnClicked,
                disabled: this.isProcessing || !this.canSwitchPlan,
            };
        },
    },
    watch: {
        products(value) {
            this.applyDefaultSelectedProduct(value);
        },
        selectedID(value) {
            let product = this.products.filter((product) => {
                if (this.userProduct.plan_type === product.plan_type) {
                    return product;
                }
            })[0];

            this.canSwitchPlan = parseInt(value) !== parseInt(product.id);

            if (!this.canSwitchPlan) {
                if (this.submitConfig.styleClass.indexOf('disabled') < 0) {
                    this.submitConfig.styleClass.push('disabled');
                }
                this.closeNotification();
            } else {
                // warn if user has active coupon code — except a forever coupon, which is
                // preserved across switch plan (not lost), so it must not trigger the warning.
                if (this.hasActiveCoupon && !this.currentSubscriptionHasForeverCoupon) {
                    this.openNotification(
                        new NotificationBarModel({
                            success: true,
                            message: this.$getLocale(
                                'if_you_currently_have_an_active_promotion_it_may_not_apply_to_your_new_plan',
                            ),
                            autoClose: false,
                        }),
                    );
                }
                if (this.submitConfig.styleClass.indexOf('disabled') > -1) {
                    this.submitConfig.styleClass.splice(
                        this.submitConfig.styleClass.indexOf('disabled'),
                        1,
                    );
                }
            }
        },
    },
    mounted() {
        this.applyDefaultSelectedProduct(this.products);
        AccountService.setHttpService(this.$http);
    },
    methods: {
        applyDefaultSelectedProduct(products) {
            if (products && this.selectedID === null) {
                let selectedProduct = {
                    id: null,
                };
                if (this.userProduct.plan_type === products[0].plan_type) {
                    selectedProduct = products.find(
                        (item) => item.plan_type !== this.userProduct.plan_type,
                    );
                } else {
                    selectedProduct = products.find((item) => item.tier === this.userProduct.tier);
                }

                this.selectedID = selectedProduct.id;
            }
        },
        enableBtnSubmit() {
            this.isProcessing = false;
        },
        disableBtnSubmit() {
            this.isProcessing = true;
        },
        cancelOnClicked() {
            this.disableBtnSubmit();
            this.$trackingService.trackAction(this.trackActionName.SWITCH_PLAN_CANCEL);
            this.redirectToAccount();
        },
        submitOnClicked() {
            let url = `${AA_XHR_BASE_PATH}switch/process/`;
            let httpData = this.buildHttpData();
            const {
                selectedProduct,
                isUpgradingToCF,
                isSwitchingToAnnual,
                notificationPageAttributeKey,
            } = this.prepDataForSubmission();

            if (selectedProduct) {
                let params = {
                    plan: selectedProduct.product_code_token,
                };
                if (this.promo && this.isValidPromoToSubmit()) {
                    params.couponCode = this.promo;
                }
                httpData = this.buildHttpData(params);

                // user is trying to switch from monthly plan to annual plan (vice versa is not allowed yet)
                if (isSwitchingToAnnual) {
                    // if user hasn't seen prompt modal and confirmed yet, show the prompt modal
                    if (!this.confirmSwitchToAnnual) {
                        this.switchToAnnualPrompt();
                        return;
                    }
                }

                this.trackSwitchPlan(selectedProduct);

                this.disableBtnSubmit();

                const submitTransaction = () => {
                    this.$http.doPost(url, httpData).then((res) => {
                        if (res.success) {
                            this.trackCrossgradeSuccess(selectedProduct);

                            this.openNotification(
                                new NotificationBarModel({
                                    messageKey: notificationPageAttributeKey,
                                }),
                            );
                            if (this.promo) {
                                this.removePromoCookieAfterApply();
                            }
                            if (isUpgradingToCF) {
                                this.hideAllAccessUpsellNavItem();
                            }

                            this.redirectToAccount();
                        } else {
                            this.trackCrossgradeFailure(res.message);

                            this.openNotification(
                                new NotificationBarModel({
                                    success: false,
                                    message: this.$getLocale(
                                        'not_enough_funds_on_the_account_add_a_credit_card_or_additional_gift_card_to_proceed',
                                    ),
                                }),
                            );

                            this.enableBtnSubmit();
                        }
                    });
                };
                submitTransaction();
            }
        },

        prepDataForSubmission() {
            let notificationPageAttributeKey = '';
            let isUpgradingToCF = false;
            let isSwitchingToAnnual = false;
            let selectedProduct = this.products.find(
                (item) => item.id === parseInt(this.selectedID),
            );

            if (selectedProduct) {
                if (this.userProduct.tier > selectedProduct.tier) {
                    notificationPageAttributeKey = 'switch_success_downgrade';
                } else if (this.userProduct.tier < selectedProduct.tier) {
                    notificationPageAttributeKey = 'switch_success_upgrade';
                    isUpgradingToCF = true;
                }

                // user is trying to switch from monthly plan to annual plan (vice versa is not allowed yet)
                if (
                    this.userProduct.plan_type === Product.TYPE_MONTHLY &&
                    selectedProduct.plan_type === Product.TYPE_ANNUAL
                ) {
                    notificationPageAttributeKey = 'switch_success_upgrade_annual';

                    isSwitchingToAnnual = true;
                }
            }

            return {
                selectedProduct,
                isUpgradingToCF,
                isSwitchingToAnnual,
                notificationPageAttributeKey,
            };
        },
        hideAllAccessUpsellNavItem() {
            let aaupsellnav = document.querySelector(
                '#global-header-container li.all-access-upsell-button',
            );

            if (aaupsellnav) {
                aaupsellnav.style.display = 'none';
            }
        },
        isValidPromoToSubmit() {
            return this.isValidPromoList[this.selectedID];
        },
        hasActiveCoupon() {
            return this.currentSubscription ? this.currentSubscription.has_active_coupon : false;
        },
        promoSuccess() {
            this.removePromoCookieAfterApply();
        },
        applyPromo(callback = () => {}) {
            if (this.isValidPromoToSubmit()) {
                this.$trackingService.trackAction(this.trackActionName.SWITCH_PLAN_UPGRADE_OFFER);

                let httpData = {
                    tk_trp: this.authToken,
                    coupon: this.promo,
                };

                AccountService.redeemCoupon(httpData)
                    .then((res) => {
                        if (!res.success) {
                            this.openNotification(
                                new NotificationBarModel({
                                    success: false,
                                    message: res.message,
                                }),
                            );
                        }
                        this.removePromoCookieAfterApply();
                        callback();
                    })
                    .catch((err) => {
                        this.openNotification(
                            new NotificationBarModel({
                                success: false,
                                message: err.message,
                            }),
                        );
                        callback();
                    });
            } else {
                callback();
            }
        },
        applyPromoAfterUpgrade() {
            this.applyPromo(this.redirectToAccount);
        },

        removePromoCookieAfterApply() {
            AccountService.removePromoCookie()
                .then((res) => {
                    // nothing to follow up with, cookie was removed
                })
                .catch((err) => {
                    // swallow error
                });
        },
        redirectToAccount() {
            this.$router.push({
                name: 'AccountMain',
            });
        },
        handleValidPromo(obj) {
            this.isValidPromoList[obj.productId] = obj.isValid;
        },
        trackSwitchPlan(selectedProduct) {
            this.$trackingService.trackAction(this.trackActionName.SWITCH_PLAN, {
                pageType: pageType.SWITCH_PLAN,
                ctaText: this.submitConfig.displayText,
                productCurrentSku: this.userProduct.product_code,
                productNewSku: selectedProduct.product_code,
                screenName: '/switch-plan/',
                switchPlanClick: 1,
            });
        },
        trackCrossgradeSuccess(selectedProduct) {
            const args = {
                pageType: pageType.CROSSGRADE,
                ctaText: this.submitConfig.displayText,
                productCurrentSku: this.userProduct.product_code,
                productNewSku: selectedProduct.product_code,
                purchaseProduct: selectedProduct.product_code,
                purchaseProductName: selectedProduct.titleForTracking,
                purchasePrice: selectedProduct.price,
                purchasePricingPlan: selectedProduct.plan_type,
                purchasePaymentMethod: this.billingInfo.paymentMethod,
                // users forfeit trial when switching plans
                productOfferPeriod: '0-day trial',
                crossgradeConfirm: 1,
            };

            // Add coupon code if present
            if (this.promo && this.isValidPromoToSubmit()) {
                args.purchasePromoCode = this.promo;
            }

            this.$trackingService.trackAction(this.trackActionName.CROSSGRADE_COMPLETE, args);
        },
        trackCrossgradeFailure(error) {
            this.$trackingService.trackLog(logType.SWITCH_PLAN_ERROR, error);
        },
    },
};
